import { type KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { scrollOptionIntoView } from './scrollOptionIntoView';

export type CustomSelectOption<T extends string> = {
  value: T;
  label: string;
  /** 任意。各選択肢に説明文を併記したい場合に指定する。 */
  description?: string;
};

type Props<T extends string> = {
  value: T;
  options: readonly CustomSelectOption<T>[];
  onChange: (value: T) => void;
  /** ドロップダウンを開く方向。入力欄の下に置く場合などは 'up'。デフォルト 'down'。 */
  openDirection?: 'up' | 'down';
  /**
   * ドロップダウンの水平方向の揃え。トリガーより幅広のメニューを出すとき、トリガーが右寄せ配置なら
   * 'right' にすると左方向に開いて右端からはみ出さない。デフォルト 'left'。
   */
  align?: 'left' | 'right';
  /** トリガーボタンの title 属性。 */
  title?: string;
  /** トリガーボタンのクラス（デフォルトを置き換える）。 */
  triggerClassName?: string;
  /** ドロップダウンメニューのクラス（幅・スクロールなど。デフォルトを置き換える）。 */
  menuClassName?: string;
};

const DEFAULT_TRIGGER_CLASS =
  'flex items-center gap-1 rounded border border-gray-400 bg-transparent py-0.5 pl-2 pr-1 text-xs text-gray-700 hover:border-gray-500 focus:outline-none';
const DEFAULT_MENU_CLASS = 'w-64 overflow-hidden';

/** セレクトボックスの開閉を示す上下シェブロンアイコン。 */
function ChevronUpDownIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.2"
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
    </svg>
  );
}

/** 選択中の項目を示すチェックマークアイコン。 */
function CheckmarkIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="2"
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

/**
 * ネイティブの select の代替となるカスタムセレクトボックス。
 * 各選択肢にラベルと説明文を併記でき、選択中の項目にチェックマークを表示する。
 * 開く方向（up / down）を指定できるので、入力欄の下など上方向に開きたい場面でも使える。
 *
 * キーボード操作はネイティブの select に合わせ、上下矢印での移動・Enter / Space での確定・
 * Escape での取り消しに対応する（開くとフォーカスは選択中の項目へ移る）。
 */
export function CustomSelect<T extends string>({
  value,
  options,
  onChange,
  openDirection = 'down',
  align = 'left',
  title,
  triggerClassName = DEFAULT_TRIGGER_CLASS,
  menuClassName = DEFAULT_MENU_CLASS,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  // キーボードフォーカスがある選択肢の index。開いている間だけ意味を持つ。
  const [activeIndex, setActiveIndex] = useState(0);
  // ドロップダウンの表示位置（fixed 配置）。未計算の間は null。
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // 閉じたあとにフォーカスを戻すためのタイマー。閉じると effect が破棄されるのでここで保持する。
  const restoreFocusTimerRef = useRef<number | undefined>(undefined);
  const id = useId();
  const listboxId = `${id}-listbox`;
  const triggerId = `${id}-trigger`;

  useEffect(() => () => window.clearTimeout(restoreFocusTimerRef.current), []);

  const selectedIndex = options.findIndex((option) => option.value === value);

  const openMenu = useCallback(() => {
    setActiveIndex(selectedIndex < 0 ? 0 : selectedIndex);
    setOpen(true);
  }, [selectedIndex]);

  // キーボード操作やメニュー内のクリックで閉じたときは、フォーカスが body へ飛ばないようトリガーへ戻す。
  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const selectOption = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) return;
      onChange(option.value);
      closeAndFocusTrigger();
    },
    [options, onChange, closeAndFocusTrigger]
  );

  // トリガーの位置からメニューの表示座標を計算する。祖先の overflow-hidden にクリップされないよう
  // メニューは body へポータルして position:fixed で配置するため、ここでビューポート基準の座標を求める。
  // align はトリガーとの水平の揃え（left: 左端合わせ / right: 右端合わせ）の初期アンカーで、
  // その後どちら側もはみ出さないようビューポート内にクランプする。
  const updatePosition = useCallback(() => {
    const trigger = containerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const gap = 4; // トリガーとメニューの隙間
    const margin = 8; // ビューポート端からの最小マージン

    // 端に寄せた値を常に [margin, viewport - margin - size] に収める。メニューがビューポートより
    // 大きく上限が margin を下回る場合でも、下限 margin を優先して画面外へ飛ばさない。
    const clamp = (value: number, size: number, viewport: number) =>
      Math.max(margin, Math.min(value, viewport - margin - size));

    const preferredTop = openDirection === 'up' ? rect.top - gap - menuRect.height : rect.bottom + gap;
    const top = clamp(preferredTop, menuRect.height, window.innerHeight);

    const anchoredLeft = align === 'right' ? rect.right - menuRect.width : rect.left;
    const left = clamp(anchoredLeft, menuRect.width, window.innerWidth);

    // scroll/resize で頻繁に呼ばれるため、座標が変わらない場合は同一参照を返して再レンダーを抑える。
    setPosition((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [align, openDirection]);

  // 開いた直後に、表示位置を計算し、選択中の項目がメニュー内に見えるよう（中央寄りに）スクロールする。
  // paint 前に位置を合わせてちらつきを防ぐため useLayoutEffect を使う。
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
    const menu = menuRef.current;
    const selected = optionRefs.current[selectedIndex];
    if (!menu || !selected) return;
    menu.scrollTop = Math.max(0, selected.offsetTop - (menu.clientHeight - selected.offsetHeight) / 2);
  }, [open, updatePosition, selectedIndex]);

  // フォーカス中の項目を実際にフォーカスし、メニューからはみ出していればその分だけスクロールする。
  // 位置計算前のメニューは visibility:hidden で、その配下の要素はフォーカスできない（focus() が無視される）。
  // そのため position が確定するまで待つ必要がある。
  useLayoutEffect(() => {
    if (!open || !position) return;
    const menu = menuRef.current;
    const active = optionRefs.current[activeIndex];
    if (!menu || !active || document.activeElement === active) return;
    // スクロールはこの直後に自前で調整するため、ブラウザ既定のスクロールは抑止する。
    active.focus({ preventScroll: true });
    scrollOptionIntoView(menu, active);
  }, [open, activeIndex, position]);

  // ドロップダウンを開いている間だけ、外側クリックで閉じ、スクロール／リサイズで位置を追従する。
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      const focusWasInMenu = menuRef.current?.contains(document.activeElement) ?? false;
      setOpen(false);
      if (!focusWasInMenu) return;
      // フォーカス中の選択肢ごとメニューが消えるとフォーカスが body に落ちる。ここで同期的に戻しても
      // mousedown の既定動作でまた外れるため、既定動作のあとに body のままだった場合だけ戻す
      // （クリック先がフォーカサブルなら body ではないので、そちらのフォーカスは奪わない）。
      restoreFocusTimerRef.current = window.setTimeout(() => {
        if (document.activeElement === document.body) triggerRef.current?.focus();
      });
    };
    document.addEventListener('mousedown', handlePointerDown);
    // capture フェーズで購読し、任意の祖先のスクロールにも追従する。
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, Math.max(0, options.length - 1)));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(Math.max(0, options.length - 1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectOption(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        closeAndFocusTrigger();
        break;
      // 先にトリガーへフォーカスを戻すことで、ブラウザ既定の順次移動がトリガー基準になる。
      // ポータル内の選択肢を起点にすると body 末尾からの移動になり、隣のコントロールへ移らない。
      case 'Tab':
        closeAndFocusTrigger();
        break;
    }
  };

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => (open ? closeAndFocusTrigger() : openMenu())}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          openMenu();
        }}
        className={triggerClassName}
      >
        <span className="truncate">{selectedOption?.label ?? value}</span>
        <ChevronUpDownIcon className="pointer-events-none size-3.5 shrink-0 text-slate-700" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={listboxId}
            role="listbox"
            aria-label={title}
            aria-labelledby={title ? undefined : triggerId}
            onKeyDown={handleMenuKeyDown}
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            className={`z-50 overscroll-none rounded border border-gray-300 bg-white shadow-lg ${menuClassName}`}
          >
            {options.map((option, index) => {
              const selected = option.value === value;
              return (
                <div
                  key={option.value}
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  role="option"
                  aria-selected={selected}
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => selectOption(index)}
                  className={`flex w-full cursor-pointer flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                    selected ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className="flex w-full items-center justify-between gap-1 text-sm font-medium text-gray-800">
                    <span className="truncate">{option.label}</span>
                    {selected && <CheckmarkIcon className="size-3.5 shrink-0 text-gray-800" />}
                  </span>
                  {option.description && (
                    <span className="text-xs leading-snug text-gray-500">{option.description}</span>
                  )}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
